/**
 * Direction-aware presentational helpers for values whose script differs from the interface language (ADR-0012 §7).
 *
 * A Persian layout renders quantities with Persian numerals, but a prompt identifier, an API key fingerprint, a
 * model name or a token count must stay Latin and must not be reordered by the bidi algorithm. Wrapping such values
 * in an element with an explicit direction is the only reliable way to achieve that.
 */

import { cn } from '@/lib/utils';
import {
  formatJalaliDate,
  formatJalaliDateTime,
  formatJalaliRelative,
  toIsoUtc,
} from '@/lib/format/jalali';
import type { DigitStyle } from '@/lib/format/numbers';
import { formatNumber } from '@/lib/format/numbers';

/** Renders Latin-only content isolated from the surrounding direction. */
export function LtrText({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return <span dir="ltr" className={cn('numeric-token inline-block', className)} {...props} />;
}

/** Props accepted by {@link NumericValue}. */
export interface NumericValueProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Value to render. */
  value: number;
  /** Which numerals to use; defaults to the interface preference at the call site. */
  digits?: DigitStyle;
  /** Whether the number is an identifier that must stay Latin and isolated. */
  asToken?: boolean;
}

/**
 * Renders a quantity with the selected numerals.
 *
 * `asToken` is for values such as identifiers or counts that are read as data rather than prose: they are rendered
 * with Latin digits and isolated direction so a copy/paste and a screen reader both produce the literal value.
 */
export function NumericValue({
  value,
  digits = 'persian',
  asToken = false,
  className,
  ...props
}: NumericValueProps) {
  if (asToken) {
    return (
      <LtrText className={className} {...props}>
        {new Intl.NumberFormat('en-US').format(value)}
      </LtrText>
    );
  }

  return (
    <span className={className} {...props}>
      {formatNumber(value, { digits })}
    </span>
  );
}

/** Props accepted by {@link JalaliDate}. */
export interface JalaliDateProps extends React.HTMLAttributes<HTMLTimeElement> {
  /** Instant as an ISO-8601 string or a `Date`. */
  value: string | Date | null | undefined;
  /**
   * Presentation style.
   *
   * Named `dateStyle` rather than `style` because `<time>` already owns `style` (the CSS attribute), and a prop that
   * silently shadows a DOM attribute is the kind of ambiguity this codebase avoids.
   */
  dateStyle?: 'numeric' | 'short' | 'medium' | 'long';
  /** Whether to append the clock time. */
  withTime?: boolean;
  /** Whether to render "3 days ago" instead of the calendar date. */
  relative?: boolean;
  /** Interface locale; controls month names and the time format. */
  locale?: 'fa' | 'en';
}

/**
 * Renders a Jalali date inside a `<time>` element.
 *
 * The machine-readable `dateTime` attribute always carries the ISO-8601 UTC value, so the semantic meaning is
 * preserved even though the visible text is a Persian calendar rendering.
 */
export function JalaliDate({
  value,
  dateStyle = 'short',
  withTime = false,
  relative = false,
  locale = 'fa',
  className,
  ...props
}: JalaliDateProps) {
  if (value === null || value === undefined) {
    return null;
  }

  const text = relative
    ? formatJalaliRelative(value, { locale })
    : withTime
      ? formatJalaliDateTime(value, { locale, style: dateStyle })
      : formatJalaliDate(value, { locale, style: dateStyle });

  return (
    <time dateTime={toIsoUtc(value)} className={className} {...props}>
      {text}
    </time>
  );
}
